import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { RemoveErrorType, SetErrorType } from '../../../../../hooks/useErrors';
import formatCurrency from '../../../../../utils/formatCurrency';
import isValueValid from '../../../../../utils/isValueValid';
import parseCurrencyStringToFloat from '../../../../../utils/parseCurrencyStringToFloat';
import { KindOfVtSpTransType, TransfersBehaviorType } from '../../types';

interface UseRoutingRulesTabI {
  setError: SetErrorType;
  removeError: RemoveErrorType;
}

export default function useRoutingRulesTab({ setError, removeError }: UseRoutingRulesTabI) {
  const [copyGoingToReturn, setCopyGoingToReturn] = useState(false);
  const [hasAffreightment, setHasAffreightment] = useState(false);
  const [hasKmLimit, setHasKmLimit] = useState(false);
  const [selectedKmLimit, setSelectedKmLimit] = useState([5, 5]);
  const [kmLimit, setKmLimit] = useState([5, 5]);
  const [selectedWalkDistance, setSelectedWalkDistance] = useState([500, 500]);
  const [walkDistance, setWalkDistance] = useState([500, 500]);
  const [transfersBehavior, setTransfersBehavior] = useState<TransfersBehaviorType>('default');

  const [compareWithDesiredVtValue, setCompareWithDesiredVtValue] = useState(false);
  const [utilizaTetoValorPadraoEmp, setUtilizaTetoValorPadraoEmp] = useState(false);
  const [aboveDesiredValueReturnsBiasTag, setAboveDesiredValueReturnsBiasTag] = useState(false);
  const [desiredVtValue, setDesiredVtValue] = useState('');
  const [chooseDefaultValueForRoutings, setChooseDefaultValueForRoutings] = useState(false);
  const [defaultValueForRoutings, setDefaultValueForRoutings] = useState('');
  const [kindOfVtSpTrans, setKindOfVtSpTrans] = useState<KindOfVtSpTransType>('vt');
  const [kindOfVtAesa, setKindOfVtAesa] = useState<string>('vt');
  const [usaRodoviarioValZero, setUsaRodoviarioValZero] = useState(false);
  const [kmRodoviarioValZero, setKmRodoviarioValZero] = useState<number | string>(0);

  const handleKmLimitSelection = useCallback((km: number[]) => {
    setKmLimit(km);
  }, []);

  const handleWalkDistanceSelection = useCallback((km: number[]) => {
    setWalkDistance(km);
  }, []);

  function handleCompareWithDesiredVtValueChange(willCompare: boolean) {
    if (willCompare) {
      setCompareWithDesiredVtValue(true);
    } else {
      removeError('desiredVtValue');
      setCompareWithDesiredVtValue(false);
      setUtilizaTetoValorPadraoEmp(false);
    }
  }

  function handleDesiredVtValueChange(event: ChangeEvent<HTMLInputElement>) {
    setDesiredVtValue(formatCurrency(event.target.value));

    if (!event.target.value && compareWithDesiredVtValue) {
      setError({ field: 'desiredVtValue', message: 'Defina um valor máximo de VT'});
    } else if ((!isValueValid(parseCurrencyStringToFloat(formatCurrency(event.target.value)), 15, 100)) && compareWithDesiredVtValue) {
      setError({ field: 'desiredVtValue', message: `Valor deve ser entre ${formatCurrency('15,00')} e ${formatCurrency('100,00')}`});
    } else {
      removeError('desiredVtValue');
    }
  }

  function handleChooseDefaultValueForRoutingsChange(willChoose: boolean) {
    if (willChoose) {
      setChooseDefaultValueForRoutings(true);
    } else {
      removeError('defaultValueForRoutings');
      setChooseDefaultValueForRoutings(false);
    }
  }

  function handleDefaultValueForRoutingsChange(event: ChangeEvent<HTMLInputElement>) {
    setDefaultValueForRoutings(formatCurrency(event.target.value));

    if (!event.target.value && compareWithDesiredVtValue) {
      setError({ field: 'defaultValueForRoutings', message: 'Defina um valor inicial de VT'});
    } else if ((!isValueValid(parseCurrencyStringToFloat(formatCurrency(event.target.value)), 3, 100)) && compareWithDesiredVtValue) {
      setError({ field: 'defaultValueForRoutings', message: `Valor deve ser entre ${formatCurrency('3.00')} e ${formatCurrency('100.00')}`});
    } else {
      removeError('defaultValueForRoutings');
    }
  }

  const handleUsaRodoviarioValZeroChange = useCallback((value: boolean) => {
    setUsaRodoviarioValZero(value);
    if (!value) {
      setKmRodoviarioValZero(0);
      removeError('kmRodoviarioValZero');
    } else {
      setKmRodoviarioValZero(1);
    }
  }, [removeError]);

  const handleKmRodoviarioValZeroChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const numericValue = value === '' ? '' : parseInt(value, 10);
    setKmRodoviarioValZero(numericValue);

    if (usaRodoviarioValZero && (numericValue === '' || numericValue <= 0)) {
      setError({ field: 'kmRodoviarioValZero', message: 'Informe uma quilometragem válida (maior que 0)' });
    } else {
      removeError('kmRodoviarioValZero');
    }
  }, [usaRodoviarioValZero, setError, removeError]);

  const isRoutingRulesTabValid = useMemo(() => (
    (((!!compareWithDesiredVtValue && !!desiredVtValue) || (!compareWithDesiredVtValue)) &&
    ((!!chooseDefaultValueForRoutings && !!defaultValueForRoutings) || (!chooseDefaultValueForRoutings)) &&
    (!usaRodoviarioValZero || (usaRodoviarioValZero && kmRodoviarioValZero !== '' && Number(kmRodoviarioValZero) > 0)))
  ), [chooseDefaultValueForRoutings, compareWithDesiredVtValue, defaultValueForRoutings, desiredVtValue, usaRodoviarioValZero, kmRodoviarioValZero]);

  return {
    copyGoingToReturn,
    setCopyGoingToReturn,
    hasAffreightment,
    setHasAffreightment,
    hasKmLimit,
    setHasKmLimit,
    selectedKmLimit,
    setSelectedKmLimit,
    kmLimit,
    setKmLimit,
    selectedWalkDistance,
    setSelectedWalkDistance,
    walkDistance,
    setWalkDistance,
    transfersBehavior,
    setTransfersBehavior,
    compareWithDesiredVtValue,
    setCompareWithDesiredVtValue,
    utilizaTetoValorPadraoEmp,
    setUtilizaTetoValorPadraoEmp,
    desiredVtValue,
    setDesiredVtValue,
    chooseDefaultValueForRoutings,
    setChooseDefaultValueForRoutings,
    defaultValueForRoutings,
    setDefaultValueForRoutings,
    kindOfVtSpTrans,
    setKindOfVtSpTrans,
    handleKmLimitSelection,
    handleWalkDistanceSelection,
    handleCompareWithDesiredVtValueChange,
    handleDesiredVtValueChange,
    handleChooseDefaultValueForRoutingsChange,
    handleDefaultValueForRoutingsChange,
    isRoutingRulesTabValid,
    kindOfVtAesa,
    setKindOfVtAesa,
    aboveDesiredValueReturnsBiasTag,
    setAboveDesiredValueReturnsBiasTag,
    usaRodoviarioValZero,
    setUsaRodoviarioValZero,
    handleUsaRodoviarioValZeroChange,
    kmRodoviarioValZero,
    setKmRodoviarioValZero,
    handleKmRodoviarioValZeroChange,
  };
}
